<section class="grid gap-4">
  <div class="flex items-center justify-between gap-3">
    <page-title [title]="isFirst() ? 'Session 1 notes' : 'Intermediate session notes'" />
    <a mat-stroked-button [routerLink]="['/interventions', interventionId]">Back to intervention</a>
  </div>

  <mat-card class="rounded-3xl border border-slate-200 p-6 shadow-lg shadow-slate-200/60">
    <form [formGroup]="form" (ngSubmit)="save()" class="grid gap-8">
      <section class="grid gap-1 text-sm text-slate-700 md:grid-cols-2">
        <span><span class="font-medium">Case number:</span> {{ intervention()?.interventionNumber }}</span>
        <span><span class="font-medium">Type:</span> {{ typeName() }}</span>
        @if (session(); as item) {
          <span><span class="font-medium">Session:</span> {{ item.sessionNumber }}</span>
          <span><span class="font-medium">Date:</span> {{ item.sessionDate | date: 'dd MMM yyyy HH:mm' }}</span>
          <span><span class="font-medium">Counsellor:</span> {{ item.affiliateName ?? 'Unknown' }}</span>
          @if (venue()) {
            <span><span class="font-medium">Venue:</span> {{ venue() }}</span>
          }
        }
        @if (note(); as saved) {
          <span class="text-slate-500">
            Submitted {{ saved.submittedDate | date: 'dd MMM yyyy HH:mm' }}. Saving again replaces these notes.
          </span>
        }
      </section>

      @if (!session()) {
        <p class="text-sm text-slate-500">Loading the session…</p>
      }

      @if (isFirst()) {
        @if (store.referralSummary().treatmentGoals.length > 0) {
          <section class="grid gap-2 rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
            <h3 class="font-semibold text-slate-900">Treatment goals indicated by the case manager</h3>
            <ol class="list-decimal pl-5">
              @for (goal of store.referralSummary().treatmentGoals; track goal.id) {
                <li>{{ goal.goal }}</li>
              }
            </ol>
          </section>
        }

        <section class="grid gap-3">
          <h3 class="font-semibold text-slate-900">Treatment goals <span class="text-red-600">*</span></h3>
          <div class="grid gap-2">
            <span class="text-sm font-medium text-slate-800">Were the treatment goals discussed with the client?</span>
            <mat-radio-group formControlName="goalsDiscussed" class="flex gap-4">
              <mat-radio-button [value]="true">Yes</mat-radio-button>
              <mat-radio-button [value]="false">No</mat-radio-button>
            </mat-radio-group>
          </div>
          @if (form.controls.goalsDiscussed.value === true) {
            <div class="grid gap-2">
              <span class="text-sm font-medium text-slate-800">Did the client agree to the goals?</span>
              <mat-radio-group formControlName="goalsAgreed" class="flex gap-4">
                <mat-radio-button [value]="true">Yes</mat-radio-button>
                <mat-radio-button [value]="false">No</mat-radio-button>
              </mat-radio-group>
            </div>
          } @else if (form.controls.goalsDiscussed.value === false) {
            <mat-form-field appearance="outline">
              <mat-label>Reason the goals were not discussed</mat-label>
              <textarea matInput rows="2" maxlength="1000" formControlName="goalsNotDiscussedReason"></textarea>
            </mat-form-field>
          }
        </section>

        <section class="grid gap-3">
          <h3 class="font-semibold text-slate-900">Severity of the presenting problem <span class="text-red-600">*</span></h3>
          <p class="text-sm text-slate-500">0 = no problem, 5 = severe.</p>
          <mat-radio-group formControlName="problemSeverity" class="flex flex-wrap gap-x-4">
            @for (value of scale; track value) {
              <mat-radio-button [value]="value">{{ value }}</mat-radio-button>
            }
          </mat-radio-group>
        </section>

        <section class="grid gap-3">
          <h3 class="font-semibold text-slate-900">Impact on functioning</h3>
          <p class="text-sm text-slate-500">
            Baseline for the areas of the client's life the problem affects. Rate each 0 (no impact) to 5 (severe impact).
          </p>
          @for (row of impacts.controls; track row; let i = $index) {
            <div class="grid items-start gap-2 md:grid-cols-[12rem_1fr_auto]" [formGroup]="row">
              <mat-form-field appearance="outline">
                <mat-label>Area</mat-label>
                <mat-select formControlName="functioningAreaId">
                  @for (area of functioningAreas(); track area.id) {
                    <mat-option [value]="area.id">{{ area.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <div class="grid gap-1">
                <mat-form-field appearance="outline">
                  <mat-label>Description</mat-label>
                  <input matInput maxlength="500" formControlName="description" />
                </mat-form-field>
                <mat-radio-group formControlName="rating" class="flex flex-wrap gap-x-3" aria-label="Rating">
                  @for (value of scale; track value) {
                    <mat-radio-button [value]="value">{{ value }}</mat-radio-button>
                  }
                </mat-radio-group>
              </div>
              @if (canEdit()) {
                <button mat-icon-button type="button" class="mt-2" aria-label="Remove area" (click)="impacts.removeAt(i)">
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </div>
          }
          @if (canEdit() && impacts.length < maxImpactRows) {
            <div><button mat-stroked-button type="button" (click)="addImpact()">Add an area</button></div>
          }
        </section>

        <section class="grid gap-3">
          <h3 class="font-semibold text-slate-900">Problems outside the scope of EAP counselling</h3>
          <p class="text-sm text-slate-500">What was done about each, and any resource the client was pointed to.</p>
          @for (row of outOfScope.controls; track row; let i = $index) {
            <div class="grid items-start gap-2 md:grid-cols-[1fr_1fr_1fr_auto]" [formGroup]="row">
              <mat-form-field appearance="outline">
                <mat-label>Problem</mat-label>
                <input matInput maxlength="500" formControlName="problem" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Intervention</mat-label>
                <input matInput maxlength="500" formControlName="intervention" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Resource</mat-label>
                <input matInput maxlength="500" formControlName="resource" />
              </mat-form-field>
              @if (canEdit()) {
                <button mat-icon-button type="button" class="mt-2" aria-label="Remove problem" (click)="outOfScope.removeAt(i)">
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </div>
          }
          @if (canEdit() && outOfScope.length < maxOutOfScopeRows) {
            <div><button mat-stroked-button type="button" (click)="addOutOfScope()">Add a problem</button></div>
          }
        </section>
      }

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">Client's coping level <span class="text-red-600">*</span></h3>
        <p class="text-sm text-slate-500">0 = not coping, 5 = coping well.</p>
        <mat-radio-group formControlName="copingLevel" class="flex flex-wrap gap-x-4">
          @for (value of scale; track value) {
            <mat-radio-button [value]="value">{{ value }}</mat-radio-button>
          }
        </mat-radio-group>
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">Risk rating <span class="text-red-600">*</span></h3>
        <mat-radio-group formControlName="riskRatingId" class="flex flex-wrap gap-x-4">
          @for (option of riskRatings(); track option.id) {
            <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
          }
        </mat-radio-group>
      </section>

      <section class="grid gap-3">
        <mat-form-field appearance="outline">
          <mat-label>Interventions provided *</mat-label>
          <textarea matInput rows="4" maxlength="4000" formControlName="interventionsProvided"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Homework given</mat-label>
          <textarea matInput rows="2" maxlength="2000" formControlName="homeworkGiven"></textarea>
        </mat-form-field>
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">Client's commitment to the process <span class="text-red-600">*</span></h3>
        <p class="text-sm text-slate-500">0 = none, 5 = fully committed.</p>
        <mat-radio-group formControlName="commitmentLevel" class="flex flex-wrap gap-x-4">
          @for (value of scale; track value) {
            <mat-radio-button [value]="value">{{ value }}</mat-radio-button>
          }
        </mat-radio-group>
      </section>

      <section class="grid gap-3">
        <mat-form-field appearance="outline">
          <mat-label>Clinical impression</mat-label>
          <textarea matInput rows="3" maxlength="4000" formControlName="clinicalImpression"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Risks to the process</mat-label>
          <textarea matInput rows="2" maxlength="2000" formControlName="processRisks"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Next steps</mat-label>
          <textarea matInput rows="2" maxlength="2000" formControlName="nextSteps"></textarea>
        </mat-form-field>
      </section>

      <section class="grid gap-3">
        @if (isFirst()) {
          <div class="grid gap-2">
            <span class="text-sm font-medium text-slate-800">Number of sessions initially required <span class="text-red-600">*</span></span>
            <mat-radio-group formControlName="initialSessionsRequired" class="flex flex-wrap gap-x-4">
              @for (option of initialSessionOptions; track option.value) {
                <mat-radio-button [value]="option.value">{{ option.label }}</mat-radio-button>
              }
            </mat-radio-group>
          </div>
        }
        <div class="md:max-w-xs">
          <date-time-picker label="Next session" formControlName="nextSessionDate" />
        </div>
        @if (needsDelayReason()) {
          <mat-form-field appearance="outline">
            <mat-label>Reason the next session is more than a week away *</mat-label>
            <input matInput maxlength="500" formControlName="nextSessionReason" />
          </mat-form-field>
        }
      </section>

      <section class="grid gap-3">
        <div class="grid gap-2">
          <span class="text-sm font-medium text-slate-800">Early termination by the client? <span class="text-red-600">*</span></span>
          <mat-radio-group formControlName="earlyTermination" class="flex gap-4">
            <mat-radio-button [value]="true">Yes</mat-radio-button>
            <mat-radio-button [value]="false">No</mat-radio-button>
          </mat-radio-group>
        </div>
        <div class="grid gap-2">
          <span class="text-sm font-medium text-slate-800">
            Is a motivation for additional sessions required? <span class="text-red-600">*</span>
          </span>
          <mat-radio-group formControlName="motivationForAdditionalSessions" class="flex gap-4">
            <mat-radio-button [value]="true">Yes</mat-radio-button>
            <mat-radio-button [value]="false">No</mat-radio-button>
          </mat-radio-group>
        </div>
        <mat-form-field appearance="outline">
          <mat-label>Additional comments</mat-label>
          <textarea matInput rows="2" maxlength="2000" formControlName="additionalComments"></textarea>
        </mat-form-field>
      </section>

      @if (canEdit()) {
        <div class="flex justify-end gap-2">
          <a mat-stroked-button [routerLink]="['/interventions', interventionId]">Cancel</a>
          <button mat-flat-button type="submit" [disabled]="!canSave()">
            @if (store.savingSessionNote()) {
              <mat-spinner diameter="20" class="mr-2" />
            }
            Save notes
          </button>
        </div>
      }
    </form>
  </mat-card>
</section>
